import { useEffect, useMemo, useRef, useState } from 'react';

import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { LoaderCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useGetEmbryosQuery,
  useGetFrozenEmbryosQuery,
} from '@/app/services/inClinic';
import { ChipSelector, DataGrid } from '@/components/common';
import { EmbryoLink } from '@/components/common/links';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSites } from '@/services/sites';
import { useContextMenu } from '@/utils/context-menu';

import { CALF_TYPE_FILTER_OPTIONS } from '../../types';
import type { Embryo, EmbryoViewMode, FreshEmbryoFilters } from '../../types';
import { roundDecimalForDisplay } from '../decimalInput';

interface EmbryoPanelProps {
  siteId: string;
  allowSiteFilter: boolean;
  embryoSiteId: string;
  onEmbryoSiteChange: (siteId: string) => void;
  impDate: string;
  filters: FreshEmbryoFilters;
  onFiltersChange: (filters: FreshEmbryoFilters) => void;
  selectedEmbryoId: string | null;
  onEmbryoSelect: (id: string | null) => void;
  queueEmpty: boolean;
  viewMode: EmbryoViewMode;
  onViewModeChange: (mode: EmbryoViewMode) => void;
  caneSearch: string;
  onCaneSearchChange: (cane: string) => void;
}

const makeEmbryoNameCellRenderer =
  (linkEnabled: boolean) => (params: ICellRendererParams<Embryo>) => {
    if (!params.data || !params.value) return params.value ?? null;
    if (!linkEnabled) return params.value as string;
    return (
      <EmbryoLink
        embryoId={params.data.embryoId}
        embryoName={params.value as string}
        isOutside={params.data.isOutsideEmbryo}
        openInNewTab
        stopPropagation
      />
    );
  };

const baseFreshCols: ColDef<Embryo>[] = [
  { field: 'semenTypeName', headerName: 'Semen', width: 80 },
  { field: 'calfTypeName', headerName: 'Calf Type', width: 90 },
  { field: 'freshStrawNumber', headerName: 'Straw#', width: 65 },
  { field: 'estrusSyncNumber', headerName: 'ES#', width: 55 },
  {
    field: 'stage',
    headerName: 'Stg',
    width: 55,
    valueFormatter: (p) => roundDecimalForDisplay(p.value),
  },
  {
    field: 'grade',
    headerName: 'Grd',
    width: 55,
    valueFormatter: (p) => roundDecimalForDisplay(p.value),
  },
  { field: 'embryoTagNumber', headerName: 'Tag#', width: 65 },
  {
    field: 'plateNumber',
    headerName: 'Plate',
    width: 60,
    valueFormatter: (p) => roundDecimalForDisplay(p.value),
  },
  { field: 'status', headerName: 'Status', width: 80 },
];

const baseFrozenCols: ColDef<Embryo>[] = [
  { field: 'semenTypeName', headerName: 'Semen', width: 80 },
  { field: 'calfTypeName', headerName: 'Calf Type', width: 90 },
  { field: 'cane', headerName: 'Cane', width: 80 },
  { field: 'strawNumber', headerName: 'Straw#', width: 65 },
  { field: 'methodName', headerName: 'Method', width: 90 },
  {
    field: 'stage',
    headerName: 'Stg',
    width: 55,
    valueFormatter: (p) => roundDecimalForDisplay(p.value),
  },
  {
    field: 'grade',
    headerName: 'Grd',
    width: 55,
    valueFormatter: (p) => roundDecimalForDisplay(p.value),
  },
  { field: 'embryoTagNumber', headerName: 'Tag#', width: 65 },
  { field: 'status', headerName: 'Status', width: 80 },
];

const embryoSiteCols: ColDef<Embryo>[] = [
  { field: 'fertSite', headerName: 'Fert Site', width: 110 },
];

const fertDateCols: ColDef<Embryo>[] = [
  { field: 'fertDate', headerName: 'Fert Date', width: 100 },
];

export function EmbryoPanel({
  siteId,
  allowSiteFilter,
  embryoSiteId,
  onEmbryoSiteChange,
  impDate,
  filters,
  onFiltersChange,
  selectedEmbryoId,
  onEmbryoSelect,
  queueEmpty,
  viewMode,
  onViewModeChange,
  caneSearch,
  onCaneSearchChange,
}: EmbryoPanelProps) {
  const { getContextMenuItems } = useContextMenu({ idKey: 'embryoId' });
  const { t } = useTranslation();
  const [showQueueWarning, setShowQueueWarning] = useState(false);
  const [caneInput, setCaneInput] = useState(caneSearch);
  const caneInputRef = useRef<HTMLInputElement>(null);
  const linkToEmbryoModule = useFeatureFlag('Link_To_Embryo_Module');
  const showFertSiteAndDate = useFeatureFlag('Implant_Show_Site_and_Fert_Date');
  const caneSearchFix = useFeatureFlag('Implant_Cane_Search_Fix');
  const showSiteColumn = allowSiteFilter || showFertSiteAndDate;
  const { data: siteOptionsRaw, isLoading: siteOptionsLoading } = useSites({
    filter: 'fertilization',
  });
  const siteOptions = siteOptionsRaw ?? [];
  const selectedSiteId = allowSiteFilter ? embryoSiteId || siteId : siteId;

  useEffect(() => {
    if (viewMode === 'frozen') {
      caneInputRef.current?.focus();
    }
  }, [viewMode]);

  const [syncedCane, setSyncedCane] = useState(caneSearch);
  if (caneSearchFix && caneSearch !== syncedCane) {
    setSyncedCane(caneSearch);
    setCaneInput(caneSearch);
  }

  const { data: freshEmbryos = [], isLoading: freshLoading } =
    useGetEmbryosQuery(
      { date: impDate, siteId: selectedSiteId },
      { skip: !selectedSiteId || !impDate },
    );

  const {
    data: frozenEmbryos = [],
    isLoading: frozenLoading,
    isFetching: frozenFetching,
    isError: frozenError,
    refetch: refetchFrozen,
  } = useGetFrozenEmbryosQuery(caneSearch, {
    skip: !caneSearch,
    refetchOnMountOrArgChange: caneSearchFix,
  });

  const filteredFreshEmbryos = useMemo(() => {
    if (filters.length === 0) return freshEmbryos;
    const lcFilters = filters.map((f) => f.toLowerCase());
    return freshEmbryos.filter((e) => {
      const calfType = (e.calfTypeName || '').toLowerCase();
      return lcFilters.some((f) => calfType.includes(f));
    });
  }, [freshEmbryos, filters]);

  const embryos = viewMode === 'fresh' ? filteredFreshEmbryos : frozenEmbryos;
  const isLoading = viewMode === 'fresh' ? freshLoading : frozenLoading;

  const freshColDefs = useMemo<ColDef<Embryo>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Mating',
        flex: 1,
        minWidth: 120,
        cellRenderer: makeEmbryoNameCellRenderer(linkToEmbryoModule),
      },
      ...baseFreshCols,
      ...(showSiteColumn ? embryoSiteCols : []),
      ...(showFertSiteAndDate ? fertDateCols : []),
    ],
    [linkToEmbryoModule, showFertSiteAndDate, showSiteColumn],
  );

  const frozenColDefs = useMemo<ColDef<Embryo>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Mating',
        flex: 1,
        minWidth: 120,
        cellRenderer: makeEmbryoNameCellRenderer(linkToEmbryoModule),
      },
      ...baseFrozenCols,
      ...(showFertSiteAndDate ? [...embryoSiteCols, ...fertDateCols] : []),
    ],
    [linkToEmbryoModule, showFertSiteAndDate],
  );

  const handleCaneSearch = () => {
    if (caneInput.trim()) {
      onCaneSearchChange(caneInput.trim());
    }
  };

  const handleCaneSearchWithRefetch = () => {
    const nextCane = caneInput.trim();
    if (!nextCane) return;
    if (nextCane === caneSearch) {
      refetchFrozen();
      return;
    }
    onCaneSearchChange(nextCane);
  };

  const onCaneSearchClick = caneSearchFix
    ? handleCaneSearchWithRefetch
    : handleCaneSearch;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex shrink-0">
          {(['fresh', 'frozen'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onViewModeChange(mode)}
              className={`border px-3 py-1 text-xs font-medium capitalize transition-colors first:rounded-l-md last:rounded-r-md ${
                viewMode === mode
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-input bg-background hover:bg-muted -ml-px'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
        {viewMode === 'fresh' && (
          <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
            <div className="max-w-[260px] min-w-0 flex-1">
              <ChipSelector<{ id: string; label: string }, string>
                options={CALF_TYPE_FILTER_OPTIONS.map((c) => ({
                  id: c,
                  label: c,
                }))}
                optionId="id"
                optionLabel="label"
                value={filters}
                onChange={(v) => onFiltersChange(v)}
                placeholder="Filter by calf type…"
                filter
                showClearAll
              />
            </div>
            {allowSiteFilter && (
              <div className="w-60">
                <ChipSelector<{ id: string; name: string }, string>
                  options={siteOptions}
                  optionId="id"
                  optionLabel="name"
                  value={selectedSiteId ? [selectedSiteId] : []}
                  onChange={(value) => {
                    onEmbryoSiteChange(value.at(-1) || '');
                    onEmbryoSelect(null);
                  }}
                  loading={siteOptionsLoading}
                  disabled={!siteId}
                  placeholder="Filter by site"
                  filter
                  showClearAll
                  maxSelectedLabels={1}
                />
              </div>
            )}
          </div>
        )}
        <span className="text-muted-foreground ml-auto text-xs">
          {embryos.length} embryo{embryos.length !== 1 ? 's' : ''}
        </span>
      </div>

      {viewMode === 'frozen' && (
        <div className="flex gap-2">
          <Input
            ref={caneInputRef}
            placeholder="Search by cane..."
            value={caneInput}
            onChange={(e) => setCaneInput(e.target.value)}
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              !(caneSearchFix && frozenFetching) &&
              onCaneSearchClick()
            }
            className="h-7 w-44 text-xs"
          />
          <Button
            size="xs"
            onClick={onCaneSearchClick}
            disabled={caneSearchFix && frozenFetching}
          >
            {caneSearchFix && frozenFetching && (
              <LoaderCircleIcon
                className="size-3.5 animate-spin"
                aria-hidden="true"
              />
            )}
            Search
          </Button>
          {caneSearch && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                setCaneInput('');
                onCaneSearchChange('');
                onViewModeChange('fresh');
                onEmbryoSelect(null);
              }}
            >
              Reset
            </Button>
          )}
        </div>
      )}

      {caneSearchFix && viewMode === 'frozen' && frozenError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive rounded border px-3 py-1.5 text-xs"
        >
          {t('inClinic.caneSearchFailed')}
        </div>
      )}

      {showQueueWarning && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          Please add a recipient to the queue first
        </div>
      )}

      <DataGrid<Embryo>
        containerClassName="ag-theme-alpine ag-compact h-[420px] overflow-hidden lg:h-auto lg:min-h-0 lg:flex-1"
        rowData={isLoading ? null : embryos}
        columnDefs={viewMode === 'fresh' ? freshColDefs : frozenColDefs}
        rowSelection="single"
        getRowId={(params) => params.data.embryoId}
        getContextMenuItems={getContextMenuItems}
        onRowClicked={(e) => {
          const embryo = e.data;
          if (!embryo) return;
          if (embryo.recipId) return;
          if (queueEmpty) {
            setShowQueueWarning(true);
            setTimeout(() => setShowQueueWarning(false), 3000);
            return;
          }
          onEmbryoSelect(embryo.embryoId);
        }}
        rowClassRules={{
          'bg-primary/20 border-l-2 border-l-primary font-medium': (params) =>
            params.data?.embryoId === selectedEmbryoId,
          'opacity-40': (params) => !!params.data?.recipId,
        }}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
        }}
        overlayNoRowsTemplate={
          viewMode === 'fresh'
            ? selectedSiteId && impDate
              ? 'No embryos for this date and site'
              : 'Select a date and site to view embryos'
            : caneSearch
              ? 'No embryos found for this cane'
              : 'Enter a cane number to search'
        }
      />
    </div>
  );
}
